import {
  wiseOwlEndpoints,
  WiseOwlCreateChatArgs,
  WiseOwlGetChatByIdArgs,
  WiseOwlUpdateChatArgs,
  WiseOwlCreateFeedbackArgs,
  WiseOwlGetChatsByUserResponseData,
  WiseOwlChatType,
  WiseOwlCreateChatResponseData,
  WiseOwlUpdateChatResponseData,
  WiseOwlGetChatSummaryByIdArgs,
  WiseOwlGetUngatedChatSummaryByIdArgs,
  WiseOwlGetAccessTokeByIngressArgs,
  WiseOwlGetAccessTokenResponseData,
  WiseOwlGetChatAboutTicketIdArgs,
  WiseOwlGetChatAboutTicketIdResponseData,
  WiseOwlIngress,
  wiseOwlIngressToServiceEndpointMap,
  WiseOwlGetChatsByUserArgs,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlGetHomeArgs,
  WiseOwlGetHomeResponseData,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";

import Client from "./client";

class WiseOwlApi extends Client {
  constructor(ingress: WiseOwlIngress) {
    const serviceEndpoint = wiseOwlIngressToServiceEndpointMap[ingress];
    super(serviceEndpoint);
  }
  /**
   * AUTH
   * - Only used for ZD/SF
   */
  getAuthToken = async ({
    headers,
    requestOptions,
    ingress,
  }: WiseOwlGetAccessTokeByIngressArgs) => {
    const response = await this.get<WiseOwlGetAccessTokenResponseData>({
      path: wiseOwlEndpoints.getAccessToken(ingress),
      headers,
      requestOptions,
    });

    return response;
  };

  /**
   * GATED METHODS
   */
  getHome = async ({
    headers,
    requestOptions,
    applicationId,
    stargateUngatedUserId,
    ingress,
  }: WiseOwlGetHomeArgs) => {
    const path = applicationId
      ? wiseOwlEndpoints.getHomeByApplicationId(applicationId)
      : stargateUngatedUserId
      ? wiseOwlEndpoints.ungatedGetHome(stargateUngatedUserId)
      : ingress === WiseOwlIngress.zendesk ||
        ingress === WiseOwlIngress.salesforce
      ? wiseOwlEndpoints.getHomeInternal
      : wiseOwlEndpoints.getHome;

    const response = await this.get<WiseOwlGetHomeResponseData>({
      path,
      headers,
      requestOptions,
    });

    return response;
  };

  getChats = async ({ headers, requestOptions }: WiseOwlGetChatsByUserArgs) => {
    const response = await this.get<WiseOwlGetChatsByUserResponseData>({
      path: wiseOwlEndpoints.getChats,
      headers,
      requestOptions,
    });

    return response;
  };

  getChatById = async ({
    applicationId,
    interruptedChatId,
    chatId,
    headers,
    requestOptions,
  }: WiseOwlGetChatByIdArgs) => {
    const path = wiseOwlEndpoints.getChatById(chatId, {
      applicationId,
      interruptedChatId,
    });

    const response = await this.get<WiseOwlChatType>({
      path,
      headers,
      requestOptions,
    });

    return response;
  };

  createChat = async ({
    body,
    headers,
    requestOptions,
  }: WiseOwlCreateChatArgs) => {
    const response = await this.post<WiseOwlCreateChatResponseData>({
      path: wiseOwlEndpoints.createChat,
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  updateChat = async ({
    chatId,
    body,
    headers,
    requestOptions,
  }: WiseOwlUpdateChatArgs) => {
    const response = await this.put<WiseOwlUpdateChatResponseData>({
      path: wiseOwlEndpoints.updateChat(chatId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  createFeedback = async ({
    chatId,
    messageId,
    body,
    headers,
    requestOptions,
  }: WiseOwlCreateFeedbackArgs) => {
    const response = await this.post<Record<string, never>>({
      path: wiseOwlEndpoints.createFeedback(chatId, messageId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  getChatSummaryById = async ({
    chatId,
    headers,
    requestOptions,
  }: WiseOwlGetChatSummaryByIdArgs) => {
    const response = await this.get<WiseOwlGetChatSummaryByIdResponseData>({
      path: wiseOwlEndpoints.getChatSummaryById(chatId),
      headers,
      requestOptions,
    });

    return response;
  };

  /**
   * UNGATED METHODS
   */
  ungatedCreateChat = async ({
    body,
    headers,
    requestOptions,
  }: WiseOwlCreateChatArgs) => {
    const response = await this.post<WiseOwlCreateChatResponseData>({
      path: wiseOwlEndpoints.ungatedCreateChat,
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  ungatedUpdateChat = async ({
    chatId,
    body,
    headers,
    requestOptions,
  }: WiseOwlUpdateChatArgs) => {
    const response = await this.put<WiseOwlUpdateChatResponseData>({
      path: wiseOwlEndpoints.ungatedUpdateChat(chatId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  ungatedCreateFeedback = async ({
    chatId,
    messageId,
    body,
    headers,
    requestOptions,
  }: WiseOwlCreateFeedbackArgs) => {
    const response = await this.post<Record<string, never>>({
      path: wiseOwlEndpoints.ungatedCreateFeedback(chatId, messageId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  ungatedGetChatSummaryById = async ({
    chatId,
    body,
    headers,
    requestOptions,
  }: WiseOwlGetUngatedChatSummaryByIdArgs) => {
    const response = await this.post<WiseOwlGetChatSummaryByIdResponseData>({
      path: wiseOwlEndpoints.ungatedGetChatSummaryById(chatId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  getChatByAboutTicketId = async ({
    aboutTicketId,
    headers,
    requestOptions,
  }: WiseOwlGetChatAboutTicketIdArgs) => {
    const query = `?aboutTicketId=${aboutTicketId}`;
    const response = await this.get<WiseOwlGetChatAboutTicketIdResponseData>({
      path: `${wiseOwlEndpoints.getChats}${query}`,
      headers,
      requestOptions,
    });

    return response;
  };
}

export default WiseOwlApi;
